export const userInfoQuery = () => `query {
    activeUser {
      name,
      email
    },
    serverInfo {
      name
      company
    }
  }`

export const projectSearchQuery = search => `query {
  activeUser {  
    projects(filter: {search: "${search}"}) {
        totalCount
        cursor
        items {
          id
          name
        }
      }
    }
  }`

export const projectSearchAllQuery = () => `query {
    activeUser {  
      projects {
          totalCount
          cursor
          items {
            id
            name
          }
        }
      }
    }`

export const projectModelsQuery = (projectId, itemsPerPage, cursor, includeVersion) => `query {
    project(id: "${projectId}"){
      models(limit: ${itemsPerPage}, cursor: ${cursor ? '"' + cursor + '"' : null}) {
        totalCount
        cursor
        items{
          id
          name
          description
          createdAt
          ${includeVersion ? "versions { items { referencedObject } }" : ""}
        }
      }
    }
  }`

export const projectModelByName = (projectId, modelName) => `query {
    project(id: "${projectId}") {
      modelByName(name: "${modelName}") {
        id,
      }
    }
}`

export const projectModelViewerResource = (projectId, modelId) => `query {
    project(id: "${projectId}") {
        viewerResources(resourceIdString: "${modelId}") {
            items {
              objectId
            }
        }
    }
}`

export const projectObjectQuery = (projectId, objectId) => `query {
    project(id: "${projectId}") {
        object(id: "${objectId}") {
            speckleType
            data
        }
    }
}`

export const projectChildrenSampleQuery = (projectId, objectId, limit) => `query {
  project(id: "${projectId}") {
    object(id: "${objectId}") {
      children(query: [{ field: "properties.builtInCategory", value: "OST_Walls", operator: "=" }], limit: ${limit}) {
        objects {
          data
        }
      }
    }
  }
}`

export const projectWallsObjectsQuery = (projectId, objectId, language = 'en', limit = 200, cursor = null) => {
  const isFr = language === 'fr';
  const category = isFr ? 'Murs' : 'Walls';
  const selectFields = isFr ? [
    "type",
    "properties.Parameters.Type Parameters.Données d'identification.Commentaires du type.value",
    "properties.Parameters.Type Parameters.Construction.Largeur.value",
    "properties.Parameters.Instance Parameters.Cotes.Surface.value",
    "properties.Parameters.Instance Parameters.Cotes.Longueur.value"
  ] : [
    "type",
    "properties.Parameters.Type Parameters.Identity Data.Type Comments.value",
    "properties.Parameters.Type Parameters.Construction.Width.value",
    "properties.Parameters.Instance Parameters.Dimensions.Volume.value",
    "properties.Parameters.Instance Parameters.Dimensions.Length.value"
  ];

  return `query {
    project(id: "${projectId}") {
      object(id: "${objectId}") {
        children (query: [{ field: "category", value: "${category}", operator: "=" }], select:[${selectFields.map(f => `"${f}"`).join(', ')}], cursor: ${cursor ? '"' + cursor + '"' : null}, limit: ${limit}) {
          cursor,
          objects {
            data
          }
        }
      }
    }
  }`
}

export const projectBracingWallsQuery = (projectId, objectId, language = 'en', limit = 200, cursor = null) => {
  const isFr = language === 'fr';
  const selectFields = isFr ? [
    "id",
    "name",
    "properties.elementId",
    "properties.Parameters.Instance Parameters.Autre.Validation.value",
    "properties.Parameters.Type Parameters.Données d'identification.Nom du type.value",
    "properties.Parameters.Instance Parameters.Cotes.Length.value",
    "properties.Parameters.Instance Parameters.Autre.Shear Number.value"
  ] : [
    "id",
    "name",
    "properties.elementId",
    "properties.Parameters.Instance Parameters.Other.Validation.value",
    "properties.Parameters.Type Parameters.Identity Data.Type Name.value",
    "properties.Parameters.Instance Parameters.Dimensions.Length.value",
    "properties.Parameters.Instance Parameters.Other.Shear Number.value"
  ];

  return `query {
    project(id: "${projectId}") {
      object(id: "${objectId}") {
        children (query: [{ field: "family", value: "OFW_WLH_WallHosted-Shear", operator: "=" }], select:[${selectFields.map(f => `"${f}"`).join(', ')}], cursor: ${cursor ? '"' + cursor + '"' : null}, limit: ${limit}) {
          cursor,
          objects {
            data
          }
        }
      }
    }
  }`
}

// Comment threads were migrated to Issues on the Speckle server (the commentThreads
// API is deprecated): threads are queried through project.issues, joined by id.
// resourceIdString is optional: pass a model id to get that model's issues, or null for
// every issue in the project, which is the only way to see "general" issues created
// without a 3D reference. loadedVersionsOnly only applies alongside a resource, so it is
// omitted with it.
export const projectIssuesQuery = (projectId, resourceIdString = null, cursor = null, limit = 25) => {
  const input = [];
  if (resourceIdString) {
    input.push(`resourceIdString: "${resourceIdString}"`, `loadedVersionsOnly: false`);
  }
  input.push(`cursor: ${cursor ? '"' + cursor + '"' : null}`, `limit: ${limit}`);
  return `query {
  project(id: "${projectId}") {
    issues(input: {
      ${input.join(',\n      ')}
    })
    {
      cursor,
      totalCount,
      items {
        id,
        createdAt,
        updatedAt,
        status,
        priority,
        dueDate,
        title,
        labels {
          id,
          name,
          hexColor
        },
        author {
          user {
            id,
            name
          }
        },
        description {
          doc,
          attachments {
            id,
            fileName
          }
        },
        replies {
          totalCount,
        },
        viewerState,
        permissions {
          canEditStatus {
            authorized
          },
          canEditPriority {
            authorized
          },
          canEditDueDate {
            authorized
          }
        }
      }
    }
  }
}`
}

export const projectIssueRepliesQuery = (projectId, issueId, cursor = null, limit = 25) => `query {
  project(id: "${projectId}") {
    issue(id: "${issueId}") {
      replies(input: {
        cursor: ${cursor ? '"' + cursor + '"' : null},
        limit: ${limit}
      })
      {
        cursor,
        totalCount,
        items {
          id,
          createdAt,
          author {
            user {
              id,
              name
            }
          },
          description {
            doc,
            attachments {
              id,
              fileName
            }
          }
        }
      },
    }
  }
}`
